// Path: ./src/app/pages/EmailNotifications/RecipientDialog.tsx
import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Switch,
  TextField,
  alpha,
} from "@mui/material";
import { NotificationTypeDto } from "@swagger/typescript-fetch-goflyer";
import { isValidEmail } from "../../../utils/isValidEmail";

export interface RecipientFormValues {
  email: string;
  notificationTypes: string[];
  enabled: boolean;
}

interface Props {
  /** Values to start from; omitted when adding a new email. */
  initialValues?: RecipientFormValues;
  types: NotificationTypeDto[];
  /** Emails already in the list (lowercased), excluding the one being edited. */
  takenEmails: string[];
  saving: boolean;
  onCancel: () => void;
  onSave: (values: RecipientFormValues) => void;
}

const BRAND = "#f58220";
const BRAND_HOVER = "#e4711f";

/** Brand colors for the MUI controls on this page, in place of MUI's default blue. */
export const brandSx = {
  containedButton: {
    bgcolor: BRAND,
    "&:hover": { bgcolor: BRAND_HOVER },
  },
  textButton: {
    color: BRAND,
    "&:hover": { bgcolor: alpha(BRAND, 0.08) },
  },
  switch: {
    "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      bgcolor: BRAND,
    },
  },
  checkbox: {
    "&.Mui-checked": { color: BRAND },
  },
  spinner: { color: BRAND },
};

const EMPTY_VALUES: RecipientFormValues = {
  email: "",
  notificationTypes: [],
  enabled: true,
};

/**
 * Add/edit form for one alert email recipient. Rendered only while open, so
 * each open starts from `initialValues`.
 */
export const RecipientDialog = ({
  initialValues,
  types,
  takenEmails,
  saving,
  onCancel,
  onSave,
}: Props) => {
  const [values, setValues] = useState<RecipientFormValues>(
    initialValues ?? EMPTY_VALUES,
  );
  const [submitted, setSubmitted] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  const email = values.email.trim().toLowerCase();
  const emailError = !email
    ? "Enter an email address."
    : !isValidEmail(email)
      ? "Enter a valid email address, like name@goflyer.ca."
      : takenEmails.includes(email)
        ? "This email is already in the list."
        : "";
  const typesError = values.notificationTypes.length
    ? ""
    : "Pick at least one notification type.";

  const toggleType = (key: string) => {
    setValues((current) => ({
      ...current,
      notificationTypes: current.notificationTypes.includes(key)
        ? current.notificationTypes.filter((type) => type !== key)
        : [...current.notificationTypes, key],
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (emailError || typesError) {
      return;
    }
    onSave({ ...values, email });
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby="recipient-dialog-title"
      TransitionProps={{ onEntered: () => emailRef.current?.focus() }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        <DialogTitle id="recipient-dialog-title">
          {initialValues ? "Edit email" : "Add email"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            inputRef={emailRef}
            fullWidth
            required
            type="email"
            label="Email address"
            value={values.email}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                email: event.target.value,
              }))
            }
            error={submitted && !!emailError}
            helperText={submitted ? emailError : " "}
            inputProps={{ maxLength: 254 }}
            sx={{
              mb: 2,
              "& .MuiInputLabel-root.Mui-focused:not(.Mui-error)": {
                color: BRAND,
              },
              "& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline":
                { borderColor: BRAND },
            }}
          />

          <FormControl
            component="fieldset"
            error={submitted && !!typesError}
            fullWidth
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FormLabel
                component="legend"
                sx={{ "&.Mui-focused:not(.Mui-error)": { color: BRAND } }}
              >
                Notifications this email receives
              </FormLabel>
              <Box>
                <Button
                  size="small"
                  sx={brandSx.textButton}
                  onClick={() =>
                    setValues((current) => ({
                      ...current,
                      notificationTypes: types.map((type) => String(type.key)),
                    }))
                  }
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  sx={brandSx.textButton}
                  onClick={() =>
                    setValues((current) => ({
                      ...current,
                      notificationTypes: [],
                    }))
                  }
                >
                  Clear
                </Button>
              </Box>
            </Box>
            <FormGroup>
              {types.map((type) => {
                const key = String(type.key);
                return (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={values.notificationTypes.includes(key)}
                        sx={brandSx.checkbox}
                        onChange={() => toggleType(key)}
                      />
                    }
                    label={type.label}
                  />
                );
              })}
            </FormGroup>
            {submitted && typesError && (
              <FormHelperText>{typesError}</FormHelperText>
            )}
          </FormControl>

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={values.enabled}
                sx={brandSx.switch}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    enabled: event.target.checked,
                  }))
                }
              />
            }
            label={values.enabled ? "Enabled" : "Disabled (receives nothing)"}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={saving} sx={brandSx.textButton}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={brandSx.containedButton}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
